import React, { useState, useCallback, useEffect } from 'react';
import { cn } from '@/lib/core/utils';
import { NumericKeypad } from './NumericKeypad';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, KeyRound } from 'lucide-react';

interface PINEntryPanelProps {
  onSubmit: (pin: string) => Promise<boolean>;
  isLoading?: boolean;
  error?: string | null;
  maxLength?: number;
  className?: string;
}

/**
 * PIN entry panel with numeric keypad
 */
export function PINEntryPanel({
  onSubmit,
  isLoading = false,
  error,
  maxLength = 6,
  className,
}: PINEntryPanelProps) {
  const [pin, setPin] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  // Clear local error when external error changes
  useEffect(() => {
    if (error) {
      setLocalError(error);
    }
  }, [error]);

  const handleDigit = useCallback((digit: string) => {
    setLocalError(null);
    setPin((prev) => {
      if (prev.length >= maxLength) return prev;
      return prev + digit;
    });
  }, [maxLength]);

  const handleBackspace = useCallback(() => {
    setLocalError(null);
    setPin((prev) => prev.slice(0, -1));
  }, []);

  const handleClear = useCallback(() => {
    setLocalError(null);
    setPin('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (pin.length < 4) {
      setLocalError('PIN must be at least 4 digits');
      return;
    }

    const success = await onSubmit(pin);
    if (!success) {
      // Error will be shown via prop
      setPin('');
    }
  }, [pin, onSubmit]);

  // Auto-submit when PIN reaches max length
  useEffect(() => {
    if (pin.length === maxLength && !isLoading) {
      handleSubmit();
    }
  }, [pin, maxLength, isLoading, handleSubmit]);

  return (
    <Card className={cn('w-full max-w-md', className)}>
      <CardHeader className="text-center pb-2">
        <CardTitle className="flex items-center justify-center gap-2 text-gray-900 dark:text-gray-100">
          <KeyRound className="w-5 h-5 text-gray-500" />
          Enter your PIN
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center pt-2">
        {/* PIN Display */}
        <div className="mb-6">
          <div className="flex gap-2 justify-center">
            {Array.from({ length: maxLength }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'w-12 h-14 rounded-lg flex items-center justify-center',
                  'border-2 transition-colors duration-150',
                  i < pin.length
                    ? 'border-green-500 bg-green-50 dark:bg-green-500/10'
                    : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-muted'
                )}
              >
                {i < pin.length && (
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Error message */}
        {localError && (
          <div className="mb-4 px-4 py-2 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-sm">{localError}</p>
          </div>
        )}

        {/* Keypad */}
        <NumericKeypad
          onDigit={handleDigit}
          onBackspace={handleBackspace}
          onClear={handleClear}
          disabled={isLoading}
        />

        {/* Submit button */}
        <Button
          onClick={handleSubmit}
          disabled={pin.length < 4 || isLoading}
          size="lg"
          className="mt-6 w-64 h-14 text-lg font-semibold"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            'Submit PIN'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
